import React, { ErrorInfo, ReactNode } from 'react';

type FallbackRender = (props: { error: Error; reset: () => void }) => ReactNode;

type Props = {
  children: ReactNode;
  /**
   * Rendered when a descendant throws during render/lifecycle. Pass a render
   * function to get access to `reset`, which clears the error and re-mounts the
   * children (useful for a "retry" affordance).
   */
  fallback: ReactNode | FallbackRender;
  onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * When any value in this array changes while in the error state, the boundary
   * resets and re-renders its children.
   */
  resetKeys?: readonly unknown[];
};

type State = {
  error: Error | null;
};

/**
 * Generic React error boundary. Catches errors thrown during render, lifecycle
 * methods and constructors of the tree below it — NOT errors in event handlers
 * or async callbacks, which must still be handled locally.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Always surface the error so a blanket boundary never silently masks bugs.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && this.haveResetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  private haveResetKeysChanged(prev?: readonly unknown[], next?: readonly unknown[]) {
    if (!prev || !next) {
      return false;
    }
    return prev.length !== next.length || prev.some((value, i) => !Object.is(value, next[i]));
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (error) {
      return typeof this.props.fallback === 'function'
        ? this.props.fallback({ error, reset: this.reset })
        : this.props.fallback;
    }
    return this.props.children;
  }
}
